import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminDataTable from '../../components/admin/AdminDataTable';
import { formatAdminDate } from '../../components/admin/adminFormatting';
import AdminPagination from '../../components/admin/AdminPagination';
import RoleBadge from '../../components/admin/RoleBadge';
import StatusBadge from '../../components/admin/StatusBadge';
import ActionsMenu from '../../components/common/ActionsMenu';
import { Button, Input, PageHeader, Select } from '@copilot/ui';
import { Field } from '../../components/common/Field';
import { Modal } from '@copilot/ui';
import { USER_ROLES } from '../../constants/roles';
import { useAuth } from '../../hooks/useAuth';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import * as adminApi from '../../services/adminApi';

const PAGE_SIZE = 20;

const ROLE_FILTERS = [
  { value: '', label: 'All Roles' },
  { value: USER_ROLES.ADMIN, label: 'Admin' },
  { value: USER_ROLES.USER, label: 'User' },
];

const STATUS_FILTERS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'deleted', label: 'Deleted' },
];

const COLUMNS = (navigate, onRestore, restoringId, onAssignPlan, onDelete, currentAdminId) => [
  {
    key: 'name',
    label: 'Name',
    render: (user) => (
      <button
        type="button"
        className="admin-table-name"
        onClick={() => navigate(`/admin/users/${user.id}`)}
      >
        {user.full_name}
      </button>
    ),
  },
  { key: 'email', label: 'Email' },
  { key: 'role', label: 'Role', render: (user) => <RoleBadge role={user.role} /> },
  {
    key: 'status',
    label: 'Status',
    render: (user) => <StatusBadge status={user.status} />,
  },
  { key: 'created_at', label: 'Created', render: (user) => formatAdminDate(user.created_at) },
  { key: 'meeting_count', label: 'Meetings' },
  {
    key: 'actions',
    label: 'Actions',
    // Restore is offered inline for deleted users so an admin who
    // trashed the wrong account can undo it straight from the list,
    // without first opening a detail page for an account the default
    // list no longer shows.
    render: (user) => (
      <ActionsMenu
        items={[
          { label: 'View', onClick: () => navigate(`/admin/users/${user.id}`) },
          // Only offered when the user has no active subscription yet -
          // changing an EXISTING plan is a detail-page action (its
          // Subscription card shows the current plan and lets it be
          // replaced), not a quick list-row shortcut.
          user.status !== 'deleted' && !user.has_active_subscription && {
            label: 'Assign Plan',
            onClick: () => onAssignPlan(user),
          },
          user.status === 'deleted' && {
            label: restoringId === user.id ? 'Restoring...' : 'Restore',
            onClick: () => onRestore(user),
            disabled: restoringId === user.id,
          },
          user.id !== currentAdminId && {
            label: 'Delete',
            onClick: () => onDelete(user),
            variant: 'danger',
          },
        ]}
      />
    ),
  },
];

function AdminUsersPage() {
  const navigate = useNavigate();
  const { user: currentAdmin } = useAuth();

  // Permanent-delete dialog: the admin must type the user's email.
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteEmail, setDeleteEmail] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  // Widens the default list to include deleted users alongside active
  // ones. Distinct from status='deleted', which shows ONLY deleted
  // users - the backend treats an explicit status as the narrower
  // request, so this checkbox is disabled while one is selected.
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [restoringId, setRestoringId] = useState(null);
  const [actionError, setActionError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  // "Assign Plan" row action - plans load once, independently of the
  // filtered/paginated user list above.
  const [plans, setPlans] = useState([]);
  const [plansError, setPlansError] = useState('');
  const [assigningUser, setAssigningUser] = useState(null);
  const [selectedPlanValue, setSelectedPlanValue] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [assignReason, setAssignReason] = useState('');

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Any filter/search change invalidates the current page - staying on
  // e.g. page 3 of a now-different, shorter result set would silently
  // show stale/empty results.
  useEffect(() => {
    setPage(1);
  }, [search, role, status, includeDeleted]);

  useEffect(() => {
    let cancelled = false;

    const handle = setTimeout(
      async () => {
        setIsLoading(true);
        setError('');

        try {
          const data = await adminApi.getAdminUsers({
            search: search.trim() || undefined,
            role: role || undefined,
            status: status || undefined,
            include_deleted: includeDeleted || undefined,
            page,
            page_size: PAGE_SIZE,
          });

          if (!cancelled) {
            setUsers(data.items);
            setTotal(data.total);
            setTotalPages(data.total_pages);
          }
        } catch (err) {
          if (!cancelled) {
            setError(
              err.status === 403
                ? 'You do not have permission to manage users.'
                : err.message || 'Unable to load users.'
            );
          }
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      },
      search ? 300 : 0
    );

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search, role, status, includeDeleted, page, reloadToken]);

  // Silent re-read: no loading/error state, last good data kept on failure.
  const refreshUsers = useCallback(
    async ({ isStale }) => {
      const data = await adminApi.getAdminUsers({
        search: search.trim() || undefined,
        role: role || undefined,
        status: status || undefined,
        include_deleted: includeDeleted || undefined,
        page,
        page_size: PAGE_SIZE,
      });

      if (!isStale()) {
        setUsers(data.items);
        setTotal(data.total);
        setTotalPages(data.total_pages);
      }
    },
    [search, role, status, includeDeleted, page]
  );

  useAutoRefresh(refreshUsers);

  useEffect(() => {
    let cancelled = false;

    async function loadPlans() {
      try {
        const result = await adminApi.getAdminPricingPlans();
        if (!cancelled) setPlans(result.filter((plan) => plan.is_active));
      } catch (err) {
        if (!cancelled) setPlansError(err.message || 'Unable to load plans.');
      }
    }

    loadPlans();

    return () => {
      cancelled = true;
    };
  }, []);

  const openDelete = (user) => {
    setActionError('');
    setDeleteError('');
    setDeleteEmail('');
    setDeleteReason('');
    setDeletingUser(user);
  };

  const closeDelete = () => setDeletingUser(null);

  const submitDelete = async () => {
    setDeleteError('');
    setIsDeleting(true);
    try {
      await adminApi.hardDeleteAdminUser(
        deletingUser.id,
        deleteEmail.trim(),
        deleteReason.trim() || null
      );
      closeDelete();
      setActionMessage('User permanently deleted.');
      setTimeout(() => setActionMessage(''), 2500);
      setReloadToken((token) => token + 1);
    } catch (err) {
      setDeleteError(err.message || 'Unable to delete this user.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestore = async (user) => {
    setActionError('');
    setRestoringId(user.id);

    try {
      await adminApi.restoreAdminUser(user.id);
      setReloadToken((token) => token + 1);
    } catch (err) {
      setActionError(err.message || 'Unable to restore this user.');
    } finally {
      setRestoringId(null);
    }
  };

  // One option per (plan, billing cycle) the plan actually prices -
  // mirrors AdminUserDetailsPage.jsx's identical "Assign Plan" dropdown.
  const planOptions = plans.flatMap((plan) => {
    const options = [];
    if (plan.monthly_price !== null) {
      options.push({
        value: `${plan.id}:monthly`,
        label: `${plan.name} — Monthly ($${plan.monthly_price})`,
        planId: plan.id,
        billingCycle: 'monthly',
      });
    }
    if (plan.yearly_price !== null) {
      options.push({
        value: `${plan.id}:yearly`,
        label: `${plan.name} — Yearly ($${plan.yearly_price})`,
        planId: plan.id,
        billingCycle: 'yearly',
      });
    }
    return options;
  });

  const openAssignPlan = (user) => {
    setActionError('');
    setAssignError('');
    setSelectedPlanValue('');
    setAssignReason('');
    setAssigningUser(user);
  };

  const closeAssignPlan = () => {
    setAssigningUser(null);
    setSelectedPlanValue('');
    setAssignReason('');
  };

  const submitAssignPlan = async () => {
    const option = planOptions.find((candidate) => candidate.value === selectedPlanValue);
    if (!option) return;

    setAssignError('');
    setIsAssigning(true);

    try {
      await adminApi.assignUserPlan(assigningUser.id, {
        plan_id: option.planId,
        billing_cycle: option.billingCycle,
        // Trimmed, and an empty box sends null rather than "" - the
        // backend stores a reasonless assignment as no reason at all,
        // not an empty string (mirrors admin_service.py's own
        // _deletion_metadata cleaning for soft-delete reasons).
        reason: assignReason.trim() || null,
      });
      closeAssignPlan();
      setActionMessage('Plan updated.');
      setTimeout(() => setActionMessage(''), 2500);
      // The row's has_active_subscription (and therefore whether
      // "Assign Plan" still shows for it) is authoritative only from
      // the backend - refetch rather than patch it locally.
      setReloadToken((token) => token + 1);
    } catch (err) {
      setAssignError(
        err.code === 'PLAN_CHANGE_REQUEST_PENDING'
          ? 'This user has a pending plan-change request. Approve or reject it first, from the Pricing page.'
          : err.message || 'Unable to assign this plan.'
      );
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Users"
        description="Manage KrishvaQ user accounts and permissions."
      />

      <div className="admin-toolbar">
        <div className="admin-toolbar-search">
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search users"
          />
        </div>

        <div className="admin-toolbar-filter">
          <Select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            aria-label="Filter by role"
          >
            {ROLE_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="admin-toolbar-filter">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter by status"
          >
            {STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <label className="admin-plan-toggle" htmlFor="users-include-deleted">
          <input
            id="users-include-deleted"
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
            disabled={Boolean(status)}
          />
          Include deleted
        </label>
      </div>

      {actionError && <div className="form-alert">{actionError}</div>}
      {actionMessage && <p className="admin-action-status success">{actionMessage}</p>}

      {error ? (
        <div className="form-alert">{error}</div>
      ) : (
        <>
          <AdminDataTable
            columns={COLUMNS(
              navigate,
              handleRestore,
              restoringId,
              openAssignPlan,
              openDelete,
              currentAdmin && currentAdmin.id
            )}
            rows={users}
            getRowKey={(user) => user.id}
            onRowClick={(user) => navigate(`/admin/users/${user.id}`)}
            isLoading={isLoading}
            loadingLabel="Loading users..."
            emptyTitle="No users found."
            emptyDescription="Try a different search or filter."
          />

          {!isLoading && (
            <AdminPagination
              page={page}
              totalPages={totalPages}
              total={total}
              itemLabel="users"
              onPageChange={setPage}
              ariaLabel="User list pages"
            />
          )}
        </>
      )}

      {deletingUser && (
        <Modal
          title="Permanently delete user?"
          onClose={() => !isDeleting && closeDelete()}
          actions={
            <>
              <Button variant="secondary" onClick={closeDelete} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={submitDelete}
                disabled={
                  isDeleting ||
                  deleteEmail.trim().toLowerCase() !== deletingUser.email.toLowerCase()
                }
              >
                {isDeleting ? 'Deleting...' : 'Delete Permanently'}
              </Button>
            </>
          }
        >
          This permanently deletes {deletingUser.full_name} and ALL of their data.
          <p className="field-help">
            Meetings, transcripts, documents, subscriptions, payments and usage
            history are erased. This cannot be undone.
          </p>
          <Field
            label={`Type ${deletingUser.email} to confirm`}
            htmlFor="delete-user-email"
          >
            <Input
              id="delete-user-email"
              value={deleteEmail}
              onChange={(e) => setDeleteEmail(e.target.value)}
              autoComplete="off"
            />
          </Field>
          <Field label="Reason (optional)" htmlFor="delete-user-reason">
            <Input
              id="delete-user-reason"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              maxLength={500}
            />
          </Field>
          {deleteError && (
            <p className="form-alert" style={{ marginTop: 'var(--space-3)' }}>
              {deleteError}
            </p>
          )}
        </Modal>
      )}

      {assigningUser && (
        <Modal
          title={`Assign Plan to ${assigningUser.full_name}`}
          onClose={() => !isAssigning && closeAssignPlan()}
          actions={
            <>
              <Button variant="secondary" onClick={closeAssignPlan} disabled={isAssigning}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submitAssignPlan}
                disabled={isAssigning || !selectedPlanValue}
              >
                {isAssigning ? 'Assigning...' : 'Assign Plan'}
              </Button>
            </>
          }
        >
          {plansError ? (
            <p className="admin-action-status">{plansError}</p>
          ) : planOptions.length === 0 ? (
            <p className="admin-action-status">No active plans available.</p>
          ) : (
            <Select
              value={selectedPlanValue}
              onChange={(e) => setSelectedPlanValue(e.target.value)}
              aria-label="Select plan"
            >
              <option value="">Select a plan…</option>
              {planOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
          <Field label="Reason (optional)" htmlFor="assign-plan-reason">
            <Input
              id="assign-plan-reason"
              value={assignReason}
              onChange={(e) => setAssignReason(e.target.value)}
              placeholder="Why is this plan being assigned?"
              maxLength={500}
            />
          </Field>
          {assignError && (
            <p className="form-alert" style={{ marginTop: 'var(--space-3)' }}>
              {assignError}
            </p>
          )}
        </Modal>
      )}
    </div>
  );
}

export default AdminUsersPage;
